/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import semverGreaterThanOrEqual from 'semver/functions/gte';
import semverCoerce from 'semver/functions/coerce';
import config from '@saucelabs/sl-util-config';
import { displayNamesMap } from '@saucelabs/sl-util-helpers';

import constants from './constants';
import { ValueOf } from '../../globalTypes';

export type DeviceDescriptorSchema = {
  abiType: string;
  apiLevel: number;
  cloudType: ValueOf<typeof constants.DEVICE_CLOUDTYPE>;
  compositeId: string;
  connectivity: ('CARRIER' | 'WIFI')[];
  cpuCores: keyof typeof constants.CPU_CORE_NAMES;
  cpuFrequency: number;
  cpuType: 'ARM' | 'X86';
  dataCenterId: keyof typeof constants.MOBILE_DATA_CENTER_NAMES;
  descriptorId: string;
  dpi: number;
  dpiName: string;
  formFactor: ValueOf<typeof constants.DEVICE_FORM_FACTOR>,
  freeOfCharge: boolean;
  hasOnScreenButtons: boolean;
  includedInPlan: boolean;
  internalStorageSize: number;
  manufacturers: string[];
  modelNumber: string;
  name: string;
  os: 'ANDROID' | 'IOS';
  osVersion: string;
  phoneNumber: string | null;
  ramSize: number;
  resolutionHeight: number;
  resolutionWidth: number;
  screenSize: number;
  supportsAppiumWebAppTesting: boolean;
}

export class DeviceDescriptor implements DeviceDescriptorSchema {
  constructor(attributes: Partial<DeviceDescriptorSchema>) {
    Object.assign(this, { ...attributes });
  }

  /**
   * Returns human readable name of the data center the device is in.
   */
  getDataCenterName() {
    return constants.MOBILE_DATA_CENTER_NAMES[this.dataCenterId];
  }

  /**
   * Returns name of the default browser on the device.
   */
  getDefaultBrowser() {
    return constants.DEFAULT_MOBILE_BROWSERS[this.os];
  }

  /**
   * Returns device specification.
   * @deprecated
   */
  getFullSpecifications() {
    return {
      OS: this.getPrettyOsAndOsVersion(),
      'API Level': this.apiLevel?.toString(),
      Screen: this.getPrettyScreen(),
      CPU: this.getPrettyCpu(),
      RAM: this.getPrettyRamSize(),
      'Internal Storage': this.getPrettyInternalStorageSize(),
      'Model Number': this.modelNumber,
      ID: this.descriptorId,
      Location: this.getDataCenterName(),
      'Carrier Connectivity': this.connectivity.includes('CARRIER') ? 'Enabled' : 'Disabled',
      ...(this.isPrivateDevice() && { 'Phone Number': this.connectivity.includes('CARRIER') ? this.phoneNumber : 'No SIM card' }),
    };
  }

  /**
   * Returns the URL of device image.
   */
  getImageUrl() {
    return `${config.apiBaseUrls.rdcCloudfront}/device-pictures/${this.descriptorId}_optimised.png`;
  }

  /**
   * Returns human readable CPU value.
   */
  getPrettyCpu() {
    return `${this.cpuType} | ${this.getPrettyCpuCores()} | ${this.cpuFrequency} MHz`;
  }

  /**
   * Returns human readable CPU cores value.
   */
  getPrettyCpuCores() {
    return `${constants.CPU_CORE_NAMES[this.cpuCores]} core`;
  }

  /**
   * Returns human readable internal storage value.
   */
  getPrettyInternalStorageSize() {
    return this.getPrettyMemorySize(this.internalStorageSize);
  }

  /**
   * Returns human readable memory size.
   */
  getPrettyMemorySize(size: number) {
    return size < 1024 ? `${size} MB` : `${Math.round(size / 1024)} GB`;
  }

  /**
   * Returns human readable OS name.
   */
  getPrettyOs() {
    return displayNamesMap[this.os.toLowerCase()] as string;
  }

  /**
   * Returns human readable OS name and version.
   */
  getPrettyOsAndOsVersion() {
    return `${this.getPrettyOs()} ${this.osVersion}`;
  }

  /**
   * Returns human readable RAM size.
   */
  getPrettyRamSize() {
    return this.getPrettyMemorySize(this.ramSize);
  }

  /**
   * Returns screen resolution (width x height).
   */
  getPrettyResolution() {
    return `${this.resolutionWidth} x ${this.resolutionHeight}`;
  }

  /**
   * Returns screen information (size, resolution and DPI(only for android devices)).
   */
  getPrettyScreen() {
    const screenInfo = `${this.screenSize}" | ${this.getPrettyResolution()}`;

    if (this.os === constants.MOBILE_PLATFORMS.IOS) {
      return screenInfo;
    }

    return `${screenInfo} | ${this.dpiName}`;
  }

  /**
   * Returns screen size and resolution.
   */
  getPrettyScreenSizeAndResolution() {
    return `${this.screenSize}" (${this.getPrettyResolution()})`;
  }

  /**
   * Determines whether the device is private.
   */
  isPrivateDevice() {
    return this.cloudType === constants.DEVICE_CLOUDTYPE.PRIVATE;
  }

  /**
   * Determines whether the device is free trial or not
   */
  isFreeTrialDevice() {
    return this.freeOfCharge;
  }

  /**
   * Determines whether the device supports WebRTC
   */
  isWebRtcSupported() {
    const version = semverCoerce(this.osVersion);
    if (this.os === constants.MOBILE_PLATFORMS.ANDROID && version) {
      return semverGreaterThanOrEqual(version, constants.MIN_WEBRTC_OS_VERSION.ANDROID);
    }

    if (this.os === constants.MOBILE_PLATFORMS.IOS && version) {
      return semverGreaterThanOrEqual(version, constants.MIN_WEBRTC_OS_VERSION.IOS);
    }

    return false;
  }

  /**
   * Determines whether given feature is supported by the device.
   */
  isFeatureSupported(feature: keyof typeof constants.MOBILE_DEVICE_FEATURES) {
    switch (feature) {
      case constants.MOBILE_DEVICE_FEATURES.CHANGE_LOCALE:
      case constants.MOBILE_DEVICE_FEATURES.DEV_TOOLS:
      case constants.MOBILE_DEVICE_FEATURES.EXECUTE_SHELL_COMMAND:
      case constants.MOBILE_DEVICE_FEATURES.TOGGLE_SYSTEM_ANIMATIONS:
      case constants.MOBILE_DEVICE_FEATURES.TOGGLE_WIFI:
        return this.os === constants.MOBILE_PLATFORMS.ANDROID;

      case constants.MOBILE_DEVICE_FEATURES.PRESS_BACK:
      case constants.MOBILE_DEVICE_FEATURES.PRESS_MENU:
      case constants.MOBILE_DEVICE_FEATURES.PRESS_SEARCH:
        return this.os === constants.MOBILE_PLATFORMS.ANDROID && !this.hasOnScreenButtons;

      case constants.MOBILE_DEVICE_FEATURES.PRESS_HOME:
        return this.os === constants.MOBILE_PLATFORMS.IOS || !this.hasOnScreenButtons;

      default:
        return false;
    }
  }
}

// trick: create interface with the same name as a class above so we don't
// need to redeclare the members
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface DeviceDescriptor extends DeviceDescriptorSchema {}

export default {
  DeviceDescriptor,
};
